import {
  NPC_ACCEL, NPC_AIM_JITTER_DEG, NPC_FIRE_RANGE, NPC_FIRE_TICKS, NPC_FRICTION,
  NPC_MAX_SPEED, NPC_PREFERRED_DIST, NPC_REPLAN_TICKS, NPC_RETREAT_DIST, NPC_STRAFE, NPC_TOUCH_PENALTY_MS,
} from './constants';
import { pathDirection, perpendicular, steerClear } from './navigation';
import { hasLineOfSight } from './obstacles';
import { accelerate, applyFriction } from './physics';
import { hashRandom } from './rng';
import type { State } from './types';
import { add, dist, normalize, rotate, scale, sub } from './vec';
import { launchProjectile, loseChessTime, opponentOf, projectileSpec } from './weapons';

export function stepNpcs(s: State): void {
  const navigation = { ...s, pillars: [...s.pillars, ...s.npcBoxes] };
  for (const npc of s.npcs) {
    const target = s.players[opponentOf(npc.ownerId)];
    const distance = dist(npc.pos, target.pos);
    // Only permanent cover hides the target, so intervening boxes can be shot open.
    const seesTarget = npc.tier > 1 && hasLineOfSight(s.map, s.pillars, npc.pos, target.pos);
    // IDs spread searches across ticks without adding a clock outside the replayable state.
    if (s.tick % NPC_REPLAN_TICKS === npc.id % NPC_REPLAN_TICKS) {
      if (seesTarget) {
        const toTarget = normalize(sub(target.pos, npc.pos));
        const approach = distance > NPC_PREFERRED_DIST ? 1 : distance < NPC_RETREAT_DIST ? -1 : 0;
        const side = npc.id % 2 === 0 ? 1 : -1;
        npc.moveDir = normalize(add(scale(toTarget, approach), scale(perpendicular(toTarget, side), NPC_STRAFE)));
      } else {
        npc.moveDir = pathDirection(navigation, npc.pos, target.pos);
      }
    }
    const dir = steerClear(navigation, npc.pos, npc.radius, npc.moveDir);
    accelerate(npc, dir, NPC_ACCEL, NPC_FRICTION, NPC_MAX_SPEED);
    applyFriction(npc, 1);
    if (npc.fireCooldownTicks > 0) npc.fireCooldownTicks--;
    if (npc.tier === 1 || npc.fireCooldownTicks > 0 || !seesTarget || distance > NPC_FIRE_RANGE) continue;
    const spec = projectileSpec('pistol');
    const aim = normalize(sub(add(target.pos, scale(target.vel, distance / spec.projectileSpeed)), npc.pos));
    const jitter = (hashRandom(s.rng, s.tick * 31 + npc.id * 131 + 2000) * 2 - 1) * NPC_AIM_JITTER_DEG * Math.PI / 180;
    s.projectiles.push(launchProjectile(npc, npc.ownerId, 'pistol', spec, rotate(aim, jitter), true));
    npc.fireCooldownTicks = NPC_FIRE_TICKS[npc.tier];
  }
}

/** Remember contacts before separation, but let weapons kill a touching NPC before it can attack. */
export function collectNpcTouches(s: State, touching: Set<number>): void {
  for (const npc of s.npcs) {
    const target = s.players[opponentOf(npc.ownerId)];
    if (dist(npc.pos, target.pos) <= npc.radius + target.radius) touching.add(npc.id);
  }
}

export function stepNpcTouches(s: State, touching: Set<number>): void {
  collectNpcTouches(s, touching);
  s.npcs = s.npcs.filter(npc => {
    if (!touching.has(npc.id)) return true;
    loseChessTime(s.players[opponentOf(npc.ownerId)], NPC_TOUCH_PENALTY_MS);
    return false;
  });
}
